// Helpers puros do funil — payload "completo" do CONTRACT §3.5. Sem nullish operator (Babel rejeita).
import { OPPORTUNITY_FASE_TYPES } from '@modules/crm/constants/Text.js'

// Funil sintético "Visão Geral": funil virtual no seletor que mescla as oportunidades
// de TODOS os funis nas 4 etapas canônicas (OPPORTUNITY_FASE_TYPES).
export const VISAO_GERAL_KEY = '__visao_geral__'
export const VISAO_GERAL_ETAPAS = [...OPPORTUNITY_FASE_TYPES]

export function normalizeFunilCompleto(value) {
  if (value && Array.isArray(value.funis)) {
    return { funis: value.funis.map((funil) => normalizeFunil(funil)) }
  }
  return { funis: [] }
}

export function normalizeFunil(funil) {
  if (!funil || typeof funil !== 'object') {
    return { etapas: [], fechados: [], sem_etapa: [] }
  }
  const etapas = Array.isArray(funil.etapas) ? funil.etapas : []
  const fechados = Array.isArray(funil.fechados) ? funil.fechados : []
  const semEtapa = Array.isArray(funil.sem_etapa) ? funil.sem_etapa : []
  return {
    ...funil,
    etapas: etapas
      .slice()
      .sort((a, b) => (a && a.ordem ? a.ordem : 0) - (b && b.ordem ? b.ordem : 0))
      .map((etapa) => normalizeEtapa(etapa)),
    fechados: fechados.map((oportunidade) => ({ ...oportunidade })),
    sem_etapa: semEtapa.map((oportunidade) => ({ ...oportunidade })),
  }
}

export function normalizeEtapa(etapa) {
  if (!etapa || typeof etapa !== 'object') return { atividades: [], oportunidades: [] }
  const atividades = Array.isArray(etapa.atividades) ? etapa.atividades : []
  const oportunidades = Array.isArray(etapa.oportunidades) ? etapa.oportunidades : []
  return {
    ...etapa,
    atividades: atividades.map((atividade) => ({ ...atividade })),
    oportunidades: oportunidades.map((oportunidade) => ({ ...oportunidade })),
  }
}

export function getFunisFromCompleto(value) {
  if (value && Array.isArray(value.funis)) return value.funis
  return []
}

export function filterFunisByTipo(funis, tipo) {
  if (!Array.isArray(funis)) return []
  if (!tipo) return funis
  return funis.filter((funil) => funil && funil.tipo_oportunidade === tipo)
}

export function extractTiposOportunidade(funis) {
  if (!Array.isArray(funis)) return []
  const vistos = {}
  const tipos = []
  funis.forEach((funil) => {
    const tipo = funil && funil.tipo_oportunidade ? funil.tipo_oportunidade : null
    if (tipo && !vistos[tipo]) {
      vistos[tipo] = true
      tipos.push(tipo)
    }
  })
  return tipos
}

export function findFunilById(value, idFunil) {
  if (!idFunil) return null
  const funis = getFunisFromCompleto(value)
  return funis.find((funil) => funil && funil.id === idFunil) || null
}

/**
 * A oportunidade de id `idOportunidade` dentro do payload `completo`, ou `null`.
 *
 * Varre os TRÊS baldes de cada funil — abertas por etapa, `fechados` e
 * `sem_etapa` —, porque a oportunidade procurada pode estar em qualquer um e
 * procurar só nas etapas devolveria `null` para toda ganha/perdida.
 *
 * O item devolvido é o MESMO que o kanban entrega ao modal de edição: com
 * `id_funil`/`id_etapa`, que o modal usa para não mover a oportunidade de
 * lugar ao salvar. Nasceu na [T029] da spec-010: o cartão do chat só tem o id,
 * e `crm-opportunities-modal.open()` não busca — abria o formulário vazio.
 */
export function findOportunidadeById(value, idOportunidade) {
  if (!idOportunidade) return null
  const alvo = String(idOportunidade)
  const funis = getFunisFromCompleto(value)
  for (let i = 0; i < funis.length; i += 1) {
    const funil = funis[i] || {}
    const etapas = Array.isArray(funil.etapas) ? funil.etapas : []
    const baldes = etapas
      .map((etapa) => (etapa && Array.isArray(etapa.oportunidades) ? etapa.oportunidades : []))
      .concat([
        Array.isArray(funil.fechados) ? funil.fechados : [],
        Array.isArray(funil.sem_etapa) ? funil.sem_etapa : [],
      ])
    for (let b = 0; b < baldes.length; b += 1) {
      const achada = baldes[b].find((o) => o && String(o.id) === alvo)
      if (achada) return achada
    }
  }
  return null
}

export function findEtapaInFunil(funil, idEtapa) {
  if (!funil || !idEtapa || !Array.isArray(funil.etapas)) return null
  return funil.etapas.find((etapa) => etapa && etapa.id === idEtapa) || null
}

export function getFunilPadrao(funis) {
  if (!Array.isArray(funis)) return null
  return funis.find((funil) => funil && funil.padrao) || null
}

// Colunas do kanban: cada etapa traz suas oportunidades abertas + as fechadas
// (ganho/perdido) daquela etapa (o card mostra a label de desfecho — não há mais
// raia "Fechados"). Fechadas sem etapa correspondente vão pra última coluna.
export function buildKanbanColumnsFromFunil(funil) {
  if (!funil || !Array.isArray(funil.etapas)) return []
  const fechados = Array.isArray(funil.fechados) ? funil.fechados : []
  const etapaIds = new Set(
    funil.etapas.map((etapa) => (etapa ? String(etapa.id) : ''))
  )
  const colunas = funil.etapas.map((etapa) => {
    const abertas = Array.isArray(etapa.oportunidades) ? etapa.oportunidades.slice() : []
    const fechadasDaEtapa = fechados.filter(
      (o) => o && String(o.id_etapa) === String(etapa.id)
    )
    return {
      id: etapa.id,
      label: etapa.nome || 'Etapa',
      cor: etapa.cor || null,
      atividades: Array.isArray(etapa.atividades) ? etapa.atividades : [],
      data: abertas.concat(fechadasDaEtapa),
    }
  })
  if (colunas.length) {
    const orfas = fechados.filter(
      (o) => o && !etapaIds.has(String(o.id_etapa))
    )
    if (orfas.length) {
      const ult = colunas.length - 1
      colunas[ult] = { ...colunas[ult], data: colunas[ult].data.concat(orfas) }
    }
  }
  return colunas
}

export function getFechadosFromFunil(funil) {
  if (!funil || !Array.isArray(funil.fechados)) return []
  return funil.fechados.slice()
}

// bucket `sem_etapa` (etapa nula/órfã) — alimenta a coluna "Sem fase".
export function getSemEtapaFromFunil(funil) {
  if (!funil || !Array.isArray(funil.sem_etapa)) return []
  return funil.sem_etapa.slice()
}

export function countOportunidadesAbertas(funil) {
  if (!funil || !Array.isArray(funil.etapas)) return 0
  return funil.etapas.reduce((total, etapa) => {
    const abertas = etapa && Array.isArray(etapa.oportunidades)
      ? etapa.oportunidades.length
      : 0
    return total + abertas
  }, 0)
}

export function sumValorAbertas(funil) {
  if (!funil || !Array.isArray(funil.etapas)) return 0
  return funil.etapas.reduce((total, etapa) => {
    const oportunidades = etapa && Array.isArray(etapa.oportunidades)
      ? etapa.oportunidades
      : []
    return (
      total +
      oportunidades.reduce((acc, o) => acc + Number((o && o.valor) || 0), 0)
    )
  }, 0)
}

// Nº de oportunidades abertas por etapa, na ordem do funil — base do gráfico de funil.
export function etapaCountsFromFunil(funil) {
  if (!funil || !Array.isArray(funil.etapas)) return []
  return funil.etapas.map((etapa) => ({
    nome: etapa && etapa.nome ? etapa.nome : 'Etapa',
    count:
      etapa && Array.isArray(etapa.oportunidades)
        ? etapa.oportunidades.length
        : 0,
  }))
}

// Desfecho do funil — abertas (pendentes) × ganhas (sucesso) × perdidas (declinado).
export function outcomeCountsFromFunil(funil) {
  const abertas = countOportunidadesAbertas(funil)
  const fechados = getFechadosFromFunil(funil)
  let ganhas = 0
  let perdidas = 0
  fechados.forEach((o) => {
    if (o && o.status === 'sucesso') ganhas += 1
    else if (o && o.status === 'declinado') perdidas += 1
  })
  return { abertas, ganhas, perdidas }
}

// Índice do bucket canônico (0..3) para uma etapa real: nome exato (trim/case-insensitive)
// vence; senão posição relativa no funil (1ª→Mapeada, última→Encerrado, meio interpolado).
export function mapEtapaToCanonicalIndex(etapa, posicao, totalEtapas) {
  const buckets = VISAO_GERAL_ETAPAS.length
  const nome = etapa && etapa.nome ? String(etapa.nome).trim().toLowerCase() : ''
  const exato = VISAO_GERAL_ETAPAS.findIndex(
    (canon) => String(canon).trim().toLowerCase() === nome
  )
  if (exato > -1) return exato
  const n = Number(totalEtapas) || 0
  if (n <= 1) return 0
  const idx = Math.round((Number(posicao) / (n - 1)) * (buckets - 1))
  return Math.max(0, Math.min(buckets - 1, idx))
}

// Constrói o funil sintético "Visão Geral" no MESMO shape de um funil real (etapas +
// fechados + sem_etapa), mesclando todos os `funis` nas 4 etapas canônicas.
export function buildVisaoGeralFunil(funis) {
  const lista = Array.isArray(funis) ? funis : []
  const buckets = VISAO_GERAL_ETAPAS.map((nome, i) => ({
    id: `vg-${i}`,
    nome,
    ordem: i,
    cor: null,
    atividades: [],
    oportunidades: [],
  }))
  const fechados = []
  const semEtapa = []
  const lastIdx = buckets.length - 1
  lista.forEach((funil) => {
    if (!funil) return
    const etapas = (Array.isArray(funil.etapas) ? funil.etapas.slice() : []).sort(
      (a, b) => ((a && a.ordem) || 0) - ((b && b.ordem) || 0)
    )
    const total = etapas.length
    // mapa etapaId → bucket canônico (p/ reposicionar as fechadas na coluna certa).
    const etapaParaBucket = {}
    etapas.forEach((etapa, posicao) => {
      const idx = mapEtapaToCanonicalIndex(etapa, posicao, total)
      if (etapa && etapa.id != null) etapaParaBucket[String(etapa.id)] = idx
      const ops =
        etapa && Array.isArray(etapa.oportunidades) ? etapa.oportunidades : []
      if (ops.length) buckets[idx].oportunidades = buckets[idx].oportunidades.concat(ops)
    })
    // Fechadas: re-etiqueta id_etapa p/ a etapa sintética do bucket da etapa original
    // (sem etapa conhecida → Encerrado). Ficam em `fechados` (somatório do resumo) e
    // o buildKanbanColumnsFromFunil as posiciona na coluna pelo id_etapa sintético.
    const funilFechados = Array.isArray(funil.fechados) ? funil.fechados : []
    funilFechados.forEach((op) => {
      if (!op) return
      const origem = etapaParaBucket[String(op.id_etapa)]
      const idx = origem === undefined ? lastIdx : origem
      fechados.push({ ...op, id_etapa: buckets[idx].id })
    })
    if (Array.isArray(funil.sem_etapa)) semEtapa.push(...funil.sem_etapa)
  })
  return {
    id: VISAO_GERAL_KEY,
    nome: 'Visão Geral',
    tipo_oportunidade: null,
    cor: null,
    ordem: -1,
    padrao: false,
    etapas: buckets,
    fechados,
    sem_etapa: semEtapa,
  }
}

export function buildEtapasDraft({ etapas, atividades, idFunil, nextKey } = {}) {
  const todasEtapas = Array.isArray(etapas) ? etapas : []
  const todasAtividades = Array.isArray(atividades) ? atividades : []
  const keyFn = typeof nextKey === 'function' ? nextKey : () => null
  return todasEtapas
    .filter((etapa) => etapa && etapa.id_funil === idFunil)
    .slice()
    .sort((a, b) => (a.ordem || 0) - (b.ordem || 0))
    .map((etapa) => ({
      _key: keyFn(),
      id: etapa.id,
      nome: etapa.nome,
      descricao: etapa.descricao,
      cor: etapa.cor,
      ordem: etapa.ordem,
      ativo: etapa.ativo,
      atividades: todasAtividades
        .filter((atividade) => atividade && atividade.id_etapa === etapa.id)
        .slice()
        .sort((a, b) => (a.ordem || 0) - (b.ordem || 0))
        .map((atividade) => ({ ...atividade, _key: keyFn() })),
    }))
}
